import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, Bell, MessageCircle, Users, Calendar, Trophy, CreditCard, Shield, User, Briefcase, Tag, UserCheck, Network, Wrench, ChevronRight, HelpCircle, Award, Database, BarChart3, AlertTriangle, FileText, Cog, Home, Building, Ticket, Star, Rss, Baby, Target, ClipboardList, Plus, Tv, LayoutDashboard } from 'lucide-react';
import { UserMenuHeader } from './UserMenuHeader';
import { ProfileSubmenu } from './ProfileSubmenu';
import { useAuth } from '../../hooks/useAuth';
import { useInternalAuth } from '../../hooks/useInternalAuth';

interface UserMenuDropdownProps {
  profilePhotoPath: string | null;
  initials: string;
  displayName: string;
  role: string;
  profileSubmenuOpen: boolean;
  onToggleProfileSubmenu: () => void;
  onClose: () => void;
  onViewProfile: () => void;
  onEditProfile: () => void;
  onAccountSettings: () => void;
  onPrivacySettings: () => void;
  onMyTickets?: () => void;
  onSignOut: () => void;
}

export const UserMenuDropdown: React.FC<UserMenuDropdownProps> = ({
  profilePhotoPath,
  initials,
  displayName,
  role,
  profileSubmenuOpen,
  onToggleProfileSubmenu,
  onClose,
  onViewProfile,
  onEditProfile,
  onAccountSettings,
  onPrivacySettings,
  onMyTickets,
  onSignOut
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [settingsSubmenuOpen, setSettingsSubmenuOpen] = useState(false);
  const [adminToolsSubmenuOpen, setAdminToolsSubmenuOpen] = useState(false);
  const [coachToolsSubmenuOpen, setCoachToolsSubmenuOpen] = useState(false);
  const [venueToolsSubmenuOpen, setVenueToolsSubmenuOpen] = useState(false);
  const [teamsSubmenuOpen, setTeamsSubmenuOpen] = useState(false);
  const [connectionsSubmenuOpen, setConnectionsSubmenuOpen] = useState(false);
  const [scorekeeperSubmenuOpen, setScorekeeperSubmenuOpen] = useState(false);
  const [gameTimeSubmenuOpen, setGameTimeSubmenuOpen] = useState(false);

  // Get user data to check age group
  const { profile } = useAuth();
  const { profile: internalProfile } = useInternalAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const isCoach = role.toLowerCase() === 'coach';
  const isVenue = role.toLowerCase() === 'venue';
  const isAdmin = role.toLowerCase() === 'admin';
  const isTeamOwner = role.toLowerCase() === 'team-owner';
  const isPlayer = role.toLowerCase() === 'player';
  const isScorekeeper = role.toLowerCase() === 'scorekeeper';
  const isParent = role.toLowerCase() === 'parent';
  const canAccessScorekeeping = isScorekeeper || role.toLowerCase() === 'support';
  const canAccessScoreApprovals = isCoach || isTeamOwner || isPlayer || isVenue; // Removed scorekeepers

  // Check if user is 13 and under based on birth_date
  const isUnder13 = () => {
    const currentProfile = profile || internalProfile;
    if (!currentProfile?.birth_date) return false;
    
    const today = new Date();
    const birth = new Date(currentProfile.birth_date);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    const dayDifference = today.getDate() - birth.getDate();
    
    // Adjust if birthday hasn't occurred this year
    const finalAge = (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) 
      ? age - 1 
      : age;
    
    return finalAge <= 13;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-16 w-72 bg-gray-900 rounded-lg shadow-lg border border-gray-700 py-2 z-50 max-h-96 overflow-y-auto"
    >
      <UserMenuHeader
        profilePhotoPath={profilePhotoPath}
        initials={initials}
        displayName={displayName}
        role={role}
      />
      
      <div className="border-t border-gray-700 mt-2 pt-2">
        {/* Dashboard Link - Available for all users */}
        <button
          onClick={() => handleNavigation('/dashboard')}
          className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <Home className="h-4 w-4" />
          Dashboard
        </button>

        {/* Game Time Submenu - For coaches, team owners, and venues */}
        {(isCoach || isTeamOwner || isVenue) && (
          <div className="relative">
            <button
              onClick={() => setGameTimeSubmenuOpen(!gameTimeSubmenuOpen)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <div className="flex items-center gap-3">
                <Target className="h-4 w-4" />
                Game Time
              </div>
              <ChevronRight className={`h-4 w-4 transition-transform ${gameTimeSubmenuOpen ? 'rotate-90' : ''}`} />
            </button>
            
            {gameTimeSubmenuOpen && (
              <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                <button
                  onClick={() => handleNavigation('/create-game')}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                >
                  <Plus className="h-3 w-3" />
                  Create Game
                </button>
                <button
                  onClick={() => handleNavigation('/my-game-forms')}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                >
                  <ClipboardList className="h-3 w-3" />
                  My Game Forms
                </button>
                {isCoach && (
                  <button
                    onClick={() => handleNavigation('/training-sessions')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <ClipboardList className="h-3 w-3" />
                    My Lessons
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* My Game Forms - For players and other roles who don't have Create Game access */}
        {!isCoach && !isTeamOwner && !isVenue && (
          <button
            onClick={() => handleNavigation('/my-game-forms')}
            className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <ClipboardList className="h-4 w-4" />
            My Game Forms
          </button>
        )}

        {/* Remove individual scorekeeper items - they belong in the Scorekeeping submenu */}

        {isUnder13() ? (
          <>
            {/* Profile Section for under 13 */}
            <ProfileSubmenu
              profileSubmenuOpen={profileSubmenuOpen}
              onToggle={onToggleProfileSubmenu}
              onViewProfile={onViewProfile}
              onEditProfile={onEditProfile}
            />

            {/* Lockeroom for under 13 */}
            <button
              onClick={() => handleNavigation('/chat')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Lockeroom
            </button>

            {/* Deals & Discounts for under 13 */}
            <button
              onClick={() => handleNavigation('/deals')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Tag className="h-4 w-4" />
              Deals & Discounts
            </button>

            {/* Points for under 13 */}
            <button
              onClick={() => handleNavigation('/points')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Award className="h-4 w-4" />
              Points
            </button>

            {/* Teams Submenu for under 13 */}
            <div className="relative">
              <button
                onClick={() => setTeamsSubmenuOpen(!teamsSubmenuOpen)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4" />
                  Teams
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${teamsSubmenuOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {teamsSubmenuOpen && (
                <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                  <button
                    onClick={() => handleNavigation('/my-teams')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <Users className="h-3 w-3" />
                    My Teams
                  </button>
                  <button
                    onClick={() => handleNavigation('/teams')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <Users className="h-3 w-3" />
                    Find a Team
                  </button>
                </div>
              )}
            </div>

            {/* Help & FAQ for under 13 */}
            <button
              onClick={() => handleNavigation('/faq')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
              Help & FAQ
            </button>

            {/* Score Approval "As Player" for under 13 */}
            <button
              onClick={() => handleNavigation('/score-approvals?role=player')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <UserCheck className="h-4 w-4" />
              Score Approval - As Player
            </button>
          </>
        ) : (
          <>
            {/* Full menu for 14+ users */}
            
            {/* Feed Link */}
            <button
              onClick={() => handleNavigation('/feed')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Rss className="h-4 w-4" />
              Feed
            </button>

            {/* Scorekeeper Tools Submenu - Only for scorekeepers */}
            {canAccessScorekeeping && (
              <div className="relative">
                <button
                  onClick={() => setScorekeeperSubmenuOpen(!scorekeeperSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Star className="h-4 w-4" />
                    Scorekeeping
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${scorekeeperSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {scorekeeperSubmenuOpen && (
                  <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                    <button
                      onClick={() => handleNavigation('/scorekeeper/dashboard')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Star className="h-3 w-3" />
                      Scorekeeper Dashboard
                    </button>
                    <button
                      onClick={() => handleNavigation('/scorekeeper/applications')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <FileText className="h-3 w-3" />
                      My Applications
                    </button>
                    <button
                      onClick={() => handleNavigation('/scorekeeper/assignments')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Calendar className="h-3 w-3" />
                      Game Assignments
                    </button>
                    <button
                      onClick={() => handleNavigation('/scoreboard')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Trophy className="h-3 w-3" />
                      Scoreboard
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Score Approvals - For coaches, team owners, players, and venues */}
            {canAccessScoreApprovals && (
              <button
                onClick={() => handleNavigation('/score-approvals')}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <UserCheck className="h-4 w-4" />
                Score Approvals
              </button>
            )}

            {/* Profile Section */}
            <ProfileSubmenu
              profileSubmenuOpen={profileSubmenuOpen}
              onToggle={onToggleProfileSubmenu}
              onViewProfile={onViewProfile}
              onEditProfile={onEditProfile}
            />

            {/* Settings Submenu */}
            <div className="relative">
              <button
                onClick={() => setSettingsSubmenuOpen(!settingsSubmenuOpen)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Settings className="h-4 w-4" />
                  Settings
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${settingsSubmenuOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {settingsSubmenuOpen && (
                <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                  <button
                    onClick={onAccountSettings}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <Settings className="h-3 w-3" />
                    Account Settings
                  </button>
                  <button
                    onClick={onPrivacySettings}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <Shield className="h-3 w-3" />
                    Privacy Settings
                  </button>
                  <button
                    onClick={() => handleNavigation('/my-tickets')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                  >
                    <Ticket className="h-3 w-3" />
                    My Support Tickets
                  </button>
                </div>
              )}
            </div>

            {/* Admin Tools Submenu - Only for admins */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={() => setAdminToolsSubmenuOpen(!adminToolsSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors border-l-2 border-red-500/30"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4" />
                    Admin Tools
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${adminToolsSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {adminToolsSubmenuOpen && (
                  <div className="ml-4 border-l border-red-500/30 pl-4 py-1 bg-red-900/10">
                    <button
                      onClick={() => handleNavigation('/admin/dashboard')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <LayoutDashboard className="h-3 w-3" />
                      Admin Dashboard
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/users')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Users className="h-3 w-3" />
                      User Management
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/analytics')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <BarChart3 className="h-3 w-3" />
                      System Analytics
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/database')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Database className="h-3 w-3" />
                      Database Tools
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/system-health')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      System Health
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/content-moderation')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <FileText className="h-3 w-3" />
                      Content Moderation
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/test-dashboard')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Wrench className="h-3 w-3" />
                      Test Dashboard
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/platform-settings')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Cog className="h-3 w-3" />
                      Platform Settings
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/media-outlets')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Tv className="h-3 w-3" />
                      Media Outlets
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin/founding-agent-applications')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-300 hover:bg-red-900/20 hover:text-red-200 transition-colors text-sm"
                    >
                      <Briefcase className="h-3 w-3" />
                      BETA Applications
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Find Venues */}
            <button
              onClick={() => handleNavigation('/venues/discover')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Building className="h-4 w-4" />
              Find Venues
            </button>

            {/* Lockeroom */}
            <button
              onClick={() => handleNavigation('/chat')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Lockeroom
            </button>

            {/* Mentions */}
            <button
              onClick={() => handleNavigation('/mentions')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Bell className="h-4 w-4" />
              Mentions
            </button>

            {/* Points */}
            <button
              onClick={() => handleNavigation('/points')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Award className="h-4 w-4" />
              Points
            </button>

            {/* Coach Tools Submenu - Only for coaches (not admins) */}
            {isCoach && !isAdmin && (
              <div className="relative">
                <button
                  onClick={() => setCoachToolsSubmenuOpen(!coachToolsSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Wrench className="h-4 w-4" />
                    Coach Tools
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${coachToolsSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {coachToolsSubmenuOpen && (
                  <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                    <button
                      onClick={() => handleNavigation('/coach-schedule')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Calendar className="h-3 w-3" />
                      View Schedule
                    </button>
                    <button
                      onClick={() => handleNavigation('/coach-analytics')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Trophy className="h-3 w-3" />
                      Performance Analytics
                    </button>
                    <button
                      onClick={() => handleNavigation('/game-management')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Settings className="h-3 w-3" />
                      Game Management
                    </button>
                    <button
                      onClick={() => handleNavigation('/upcoming-games')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Calendar className="h-3 w-3" />
                      Upcoming Games
                    </button>
                    <button
                      onClick={() => handleNavigation('/team-players')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <UserCheck className="h-3 w-3" />
                      Manage Players
                    </button>
                    <button
                      onClick={() => handleNavigation('/team-coaches')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Shield className="h-3 w-3" />
                      Manage Coaches
                    </button>
                    <button
                      onClick={() => handleNavigation('/player-stats')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <BarChart3 className="h-3 w-3" />
                      Player Stats
                    </button>
                    <button
                      onClick={() => handleNavigation('/analytics-dashboard')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <BarChart3 className="h-3 w-3" />
                      Analytics
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Venue Tools Submenu - Only for venues */}
            {isVenue && (
              <div className="relative">
                <button
                  onClick={() => setVenueToolsSubmenuOpen(!venueToolsSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4" />
                    Venue Tools
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${venueToolsSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {venueToolsSubmenuOpen && (
                  <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                    <button
                      onClick={() => handleNavigation('/venue-management')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Building className="h-3 w-3" />
                      Venue Management
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Teams Submenu - Show for non-admin, non-parent users */}
            {!isAdmin && !isParent && (
              <div className="relative">
                <button
                  onClick={() => setTeamsSubmenuOpen(!teamsSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4" />
                    Teams
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${teamsSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {teamsSubmenuOpen && (
                  <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                    <button
                      onClick={() => handleNavigation('/teams')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Users className="h-3 w-3" />
                      My Teams
                    </button>
                    <button
                      onClick={() => handleNavigation('/teams-search')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Users className="h-3 w-3" />
                      Find a Team
                    </button>
                    {isCoach && (
                      <button
                        onClick={() => handleNavigation('/teams')}
                        className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                      >
                        <Users className="h-3 w-3" />
                        Create Team
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Child Management - Only for parents */}
            {isParent && (
              <button
                onClick={() => handleNavigation('/child-management')}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Baby className="h-4 w-4" />
                Child
              </button>
            )}

            {/* Connections Submenu - Show for non-admin users */}
            {!isAdmin && (
              <div className="relative">
                <button
                  onClick={() => setConnectionsSubmenuOpen(!connectionsSubmenuOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Network className="h-4 w-4" />
                    Connections
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${connectionsSubmenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {connectionsSubmenuOpen && (
                  <div className="ml-4 border-l border-gray-700 pl-4 py-1">
                    <button
                      onClick={() => handleNavigation('/connections')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Network className="h-3 w-3" />
                      My Connections
                    </button>
                    <button
                      onClick={() => handleNavigation('/user-discovery')}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
                    >
                      <Network className="h-3 w-3" />
                      Find Connections
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Deals & Discounts - Show for non-admin users */}
            {!isAdmin && (
              <button
                onClick={() => handleNavigation('/deals')}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Tag className="h-4 w-4" />
                Deals & Discounts
              </button>
            )}

            {/* Help & FAQ */}
            <button
              onClick={() => handleNavigation('/faq')}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
              Help & FAQ
            </button>
          </>
        )}

        {/* Sign Out for all users */}
        <div className="border-t border-gray-700 mt-2 pt-2">
          <button
            onClick={onSignOut}
            className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-red-400 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};
